import type { PaymentMethod } from '@neon-one/auctions-types';

import { methodTypeLabel } from './paymentMethodLabel';

// GT1-353 — payment line wording. Cash/check payments must not read
// "Charged ... to Card on File".
//
// GT1-488 — nor must a bank transfer, a wallet, or a terminal swipe. The old
// default branch produced "Charged $120.00 to Visa ****4242" for EVERY
// non-cash/check method, including ACH and PayPal, using whatever card label
// the caller happened to have on hand.
//
// Hoisted from features/checkout/lib/paymentLine.ts so PayBanner (shell)
// can share the same branching as ReceiptPage (features/checkout) without
// crossing the no-cross-feature-import / no-feature-deep-import lint rules.
export interface PaymentLineParts {
  verb: 'Paid' | 'Charged';
  // Text after the amount, e.g. "in Cash" / "with check number 1042" /
  // "to Visa ****4242" / "to your bank account" / "with PayPal".
  // Empty for a check payment with no reference.
  tail: string;
}

export function paymentLineParts(
  method: PaymentMethod | null,
  reference: string | null,
  cardLabel: string,
): PaymentLineParts {
  if (method === 'cash') {
    return { verb: 'Paid', tail: 'in Cash' };
  }
  if (method === 'check') {
    return {
      verb: 'Paid',
      tail: reference ? `with check number ${reference}` : '',
    };
  }
  if (method === 'wire') {
    return {
      verb: 'Paid',
      tail: reference ? `by wire transfer, reference ${reference}` : 'by wire transfer',
    };
  }
  // ACH debits leave the bidder's bank, not a card — and the card label a
  // caller passes in is often a completely unrelated saved card.
  if (method === 'ach') {
    return { verb: 'Charged', tail: 'to your bank account' };
  }
  // Wallets own their brand name; there is no last4 to show.
  if (
    method === 'apple_pay' ||
    method === 'google_pay' ||
    method === 'paypal' ||
    method === 'venmo'
  ) {
    return { verb: 'Charged', tail: `with ${methodTypeLabel(method)}` };
  }
  if (method === 'terminal') {
    return { verb: 'Paid', tail: 'by card in person' };
  }
  // 'card' and null (method not recorded) keep the original card wording.
  return { verb: 'Charged', tail: `to ${cardLabel}` };
}
